/**
 * Font Configuration
 *
 * Provides environment-aware font URLs for OpenDyslexic fonts hosted in Supabase Storage.
 *
 * The font URLs are dynamically generated based on the current environment's Supabase URL.
 */

/**
 * Get the base URL for font storage based on current environment
 * @returns Base URL for font storage bucket
 */
export const getFontStorageUrl = (): string => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

  if (!supabaseUrl) {
    console.warn('VITE_SUPABASE_URL not found, using production font storage URL');
    return 'https://hyvzrfzdcajcgsoxefni.supabase.co/storage/v1/object/public/fonts/opendyslexic';
  }

  // Extract project ID from Supabase URL
  // Format: https://PROJECT_ID.supabase.co
  const match = supabaseUrl.match(/https:\/\/([^.]+)\.supabase\.co/);
  const projectId = match?.[1];

  if (!projectId) {
    console.warn('Could not extract project ID from VITE_SUPABASE_URL, using production font storage URL');
    return 'https://hyvzrfzdcajcgsoxefni.supabase.co/storage/v1/object/public/fonts/opendyslexic';
  }

  return `https://${projectId}.supabase.co/storage/v1/object/public/fonts/opendyslexic`;
};

/**
 * Inject @font-face rules into the document
 * This should be called early in the application lifecycle (e.g., in main.tsx)
 */
export const injectOpenDyslexicFonts = (): void => {
  const baseUrl = getFontStorageUrl();

  const fontFaceCSS = `
    /* OpenDyslexic Regular */
    @font-face {
      font-family: 'OpenDyslexic';
      font-style: normal;
      font-weight: 400;
      font-display: swap;
      src: url('${baseUrl}/OpenDyslexic-Regular.otf') format('opentype');
    }

    /* OpenDyslexic Bold */
    @font-face {
      font-family: 'OpenDyslexic';
      font-style: normal;
      font-weight: 700;
      font-display: swap;
      src: url('${baseUrl}/OpenDyslexic-Bold.otf') format('opentype');
    }

    /* OpenDyslexic Italic */
    @font-face {
      font-family: 'OpenDyslexic';
      font-style: italic;
      font-weight: 400;
      font-display: swap;
      src: url('${baseUrl}/OpenDyslexic-Italic.otf') format('opentype');
    }

    /* OpenDyslexic Bold Italic */
    @font-face {
      font-family: 'OpenDyslexic';
      font-style: italic;
      font-weight: 700;
      font-display: swap;
      src: url('${baseUrl}/OpenDyslexic-BoldItalic.otf') format('opentype');
    }
  `;

  // Create style element and inject into document head
  const styleElement = document.createElement('style');
  styleElement.id = 'opendyslexic-fonts';
  styleElement.textContent = fontFaceCSS;

  // Remove existing style element if present (for hot module replacement)
  const existing = document.getElementById('opendyslexic-fonts');
  if (existing) {
    existing.remove();
  }

  document.head.appendChild(styleElement);

  console.info(`OpenDyslexic fonts loaded from: ${baseUrl}`);
};

/**
 * Inject environment-aware preconnect hint for Supabase
 * Improves performance by establishing early connection to Supabase storage
 */
export const injectSupabasePreconnect = (): void => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

  if (!supabaseUrl) {
    console.warn('VITE_SUPABASE_URL not found, skipping Supabase preconnect');
    return;
  }

  // Check if preconnect already exists
  const existingPreconnect = document.querySelector(`link[rel="preconnect"][href="${supabaseUrl}"]`);
  if (existingPreconnect) {
    console.info(`Supabase preconnect already exists for: ${supabaseUrl}`);
    return;
  }

  // Create preconnect link element
  const preconnect = document.createElement('link');
  preconnect.rel = 'preconnect';
  preconnect.href = supabaseUrl;
  preconnect.crossOrigin = 'anonymous';

  // Insert as first link in head for earliest connection
  const firstLink = document.head.querySelector('link');
  if (firstLink) {
    document.head.insertBefore(preconnect, firstLink);
  } else {
    document.head.appendChild(preconnect);
  }

  console.info(`Supabase preconnect injected for: ${supabaseUrl}`);
};
